import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Label from '@adsk/bim360-matrix-react-label';
import Loader from '@adsk/bim360-matrix-react-loader';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';
import ThumbnailStrip from '@adsk/bim360-matrix-react-thumbnail-strip';
import i18n from '../../utils/i18n';
import {attachmentAttributesType, issueAttributesType} from '../../constants/proptype-validation';
import IssuePreviewDetailsTabAttachmentContainer from './IssuePreviewDetailsTabAttachmentContainer';
import {getAttachmentsList, getHasMoreAttachments, getIsLoadingAttachments, getThumbnails} from '../../app/selectors';
import {connect} from '../../app/connect';
import {ATTACHMENTS_ATTRIBUTES, OSS_URN_TYPE} from '../../constants/attachments';
import {isImageAttachment} from '../../utils/attachmentsHelper';
import {fetchAttachmentsAction} from '../../app/actions';

const slidesToShow = 5;
const defaultTooltipStyles = {paddingBottom: '10px'};
const isImageOssAttachment = attachment =>
  attachment[ATTACHMENTS_ATTRIBUTES.URN_TYPE] === OSS_URN_TYPE && isImageAttachment(attachment);

const loadMoreThreshold = slidesToShow + 1;

const IssuePreviewDetailsTabAttachmentsSection = ({
  issue,
  attachments,
  hasMoreAttachments,
  onClickThumbnail,
  isLoadingAttachments,
  thumbnails,
  dispatch,
  container
}) => {
  const [thumbnailSliderIndex, setThumbnailSliderIndex] = useState(0);
  const loadMore = useCallback(() => {
    dispatch(fetchAttachmentsAction({issueId: issue.id, offset: attachments.length}));
  }, [attachments.length, dispatch, issue.id]);

  const photoAttachments = attachments.filter(isImageOssAttachment);
  useEffect(() => {
    if (
      hasMoreAttachments &&
      !isLoadingAttachments &&
      thumbnailSliderIndex + loadMoreThreshold > photoAttachments.length
    ) {
      loadMore();
    }
  }, [
    hasMoreAttachments,
    loadMore,
    photoAttachments.length,
    thumbnailSliderIndex,
    attachments.length, //necessary in case a complete batch of non-image attachments was loaded
    isLoadingAttachments
  ]);

  return (
    <div className="IssuePreviewDetailsTabAttachmentsSection">
      <div className="IssuePreviewDetailsTabAttachmentsSection__attachments">
        <div className="IssuePreviewDetailsTabAttachmentsSection__label IssueFieldInlineEdit__label-container">
          <Label className="IssueFieldInlineEdit__label">{i18n.t('preview.photos.title')}</Label>
        </div>
        <div className="IssuePreviewDetailsTabAttachmentsSection__attachments-thumbnail-strip-container">
          {attachments.length === 0 && isLoadingAttachments ? (
            <Loader className="IssuePreviewDetailsTabAttachmentsSection__attachments-loader" />
          ) : (
            <ThumbnailStrip
              className="IssuePreviewDetailsTabAttachmentsSection__attachments-thumbnail-strip"
              slidesToShow={slidesToShow}
              slidesToScroll={2}
              lazyLoad={false}
              variableWidth
              afterChange={setThumbnailSliderIndex}
            >
              {[
                ...photoAttachments.map((attachment, index) =>
                  thumbnails[attachment.urn] ? (
                    <div key={attachment.id}>
                      <IssuePreviewDetailsTabAttachmentContainer
                        thumbnail={thumbnails[attachment.urn]}
                        onClickThumbnail={() => {
                          onClickThumbnail(photoAttachments, index);
                        }}
                      />
                      <OverflowTooltip
                        style={defaultTooltipStyles}
                        container={container}
                        autoTipOnEllipsis
                        getTargetProps={() => ({
                          className: 'IssuePreviewDetailsTabAttachmentsSection__attachment-name'
                        })}
                      >
                        {attachment.name}
                      </OverflowTooltip>
                    </div>
                  ) : (
                    <Loader className="IssuePreviewDetailsTabAttachmentsSection__attachments-thumbnail-loader" />
                  )
                ),
                ...(isLoadingAttachments
                  ? [
                    <Loader
                      key="loading-more"
                      className="IssuePreviewDetailsTabAttachmentsSection__attachments-thumbnail-loader"
                    />
                  ]
                  : [])
              ]}
            </ThumbnailStrip>
          )}
        </div>
      </div>
    </div>
  );
};

IssuePreviewDetailsTabAttachmentsSection.displayName = 'IssuePreviewDetailsTabAttachmentsSection';

IssuePreviewDetailsTabAttachmentsSection.propTypes = {
  issue: issueAttributesType.isRequired,
  attachments: PropTypes.arrayOf(attachmentAttributesType).isRequired,
  onClickThumbnail: PropTypes.func.isRequired,
  isLoadingAttachments: PropTypes.bool.isRequired,
  hasMoreAttachments: PropTypes.bool.isRequired,
  thumbnails: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  container: PropTypes.object
};

const mapStateToProps = state => ({
  attachments: getAttachmentsList(state),
  thumbnails: getThumbnails(state),
  isLoadingAttachments: getIsLoadingAttachments(state),
  hasMoreAttachments: getHasMoreAttachments(state)
});

export default connect(mapStateToProps)(IssuePreviewDetailsTabAttachmentsSection);
